import { Navigation, ChevronDown } from 'lucide-react';

import {
  FONT_WEIGHTS,
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  RADIUS_SCALE,
  SIZE_SCALE,
  TYPE_SCALE,
  COLOR_SCALE,
  INTERACTIVE_STATES,
  MARGIN_BOTTOM,
  PADDING_BOTTOM,
  BORDER_WIDTH,
  FLEX,
  FLEX_ALIGN,
  FLEX_JUSTIFY,
  TEXT_ALIGNMENT,
  OVERFLOW,
} from '@/components/design-system/tokens';

interface ProviderSection {
  id: string;
  label: string;
  count?: number;
}

interface JumpSectionProps {
  providerSections: ProviderSection[];
  activeSection: string;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate: (id: string) => void;
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string;
}

export const JumpSection = ({
  providerSections,
  activeSection,
  isOpen,
  onToggle,
  onNavigate,
  t,
}: JumpSectionProps) => {
  if (providerSections.length === 0) return null;

  return (
    <div className={`${MARGIN_BOTTOM['8']} ${PADDING_BOTTOM['4']}`}>
      <div className={`${COLOR_SCALE.surface} ${RADIUS_SCALE.xl} overflow-hidden`}>
        <button
          onClick={onToggle}
          className={`${SIZE_SCALE.full} ${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between} ${PADDING_X['4']} ${PADDING_Y['4']} hover:${COLOR_SCALE['surface-muted']} transition-colors`}
        >
          <div className={`${FLEX.flex} ${FLEX_ALIGN.center} ${GAP_SCALE['3']}`}>
            <div
              className={`${SIZE_SCALE.w9} ${SIZE_SCALE['9']} ${RADIUS_SCALE.lg} bg-gradient-to-br from-purple-50 to-blue-50 ${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.center}`}
            >
              <Navigation
                className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} ${COLOR_SCALE['purple-600']}`}
              />
            </div>
            <div className={`${TEXT_ALIGNMENT.left}`}>
              <p className={`${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}>
                {t('provider_detail', 'jump', 'Jump')}
              </p>
              <p
                className={`${TYPE_SCALE.xs} ${FONT_WEIGHTS.normal} ${COLOR_SCALE['admin-muted-text']}`}
              >
                {t('mobile_menu', 'jump_helper', 'Navigate to sections')}
              </p>
            </div>
          </div>
          <ChevronDown
            className={`${SIZE_SCALE.w5} ${SIZE_SCALE['5']} ${COLOR_SCALE['icon-muted']} transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div
            className={`${BORDER_WIDTH.thin} ${COLOR_SCALE['admin-border']} bg-gradient-to-b from-gray-50 to-white ${PADDING_X['2']} ${PADDING_Y['2']} max-h-96 ${OVERFLOW['y-auto']}`}
          >
            {providerSections.map(section => {
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => onNavigate(section.id)}
                  className={`${SIZE_SCALE.full} ${TEXT_ALIGNMENT.left} ${PADDING_X['3']} ${PADDING_Y['3']} ${RADIUS_SCALE.lg} ${INTERACTIVE_STATES.transitions.all} ${BORDER_WIDTH['1']} ${
                    isActive
                      ? '${COLOR_SCALE["surface-subtle"]} ${BORDER_WIDTH["4"]} ${COLOR_SCALE.foreground} ${FONT_WEIGHTS.semibold} shadow-sm'
                      : 'border-transparent hover:${COLOR_SCALE.surface} ${COLOR_SCALE["gray-700"]} hover:${COLOR_SCALE.foreground}'
                  }`}
                >
                  <div className={`${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between}`}>
                    <span
                      className={`${TYPE_SCALE.sm} ${FONT_WEIGHTS.normal} ${COLOR_SCALE.foreground}`}
                    >
                      {section.label}
                    </span>
                    {section.count !== undefined && (
                      <span
                        className={`${TYPE_SCALE.xs} ${PADDING_X['2']} ${PADDING_Y['0.5']} ${RADIUS_SCALE.full} ${FONT_WEIGHTS.normal} ${
                          isActive
                            ? '${COLOR_SCALE["shortcuts-bg"]} ${COLOR_SCALE["shortcuts-text"]}'
                            : '${COLOR_SCALE["skeleton-bg"]} ${COLOR_SCALE["gray-700"]}'
                        }`}
                      >
                        {section.count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
